import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { getUnmePaymentRecoveryPath } from '../utils/unmePaymentRecovery';
import { getUnmePaymentLoginScope, readPendingUnmeOrder, savePendingUnmeOrder, clearPendingUnmeOrder } from '../utils/unmeOrderRecoveryStorage';

export const UNME_FALLBACK_ENABLED = process.env.REACT_APP_UNME_FALLBACK_ENABLED === '1';
const HANDLED = 'UNME_RETRY_HANDLED';

export function isHandledUnmeRetry(error) {
  return error?.code === HANDLED;
}

export function safeUnmePaymentMessage(message, fallback = '결제가 승인되지 않았습니다. 카드정보를 확인해 주세요.') {
  const text = String(message || '').trim();
  return text && text.length <= 200 && !/bad decrypt|decrypt|openssl|stack|exception|error:/i.test(text) ? text : fallback;
}

function handled(blocked = false) {
  const error = new Error('결제 결과를 확인해 주세요.');
  error.code = HANDLED;
  error.blocked = blocked;
  return error;
}

function responseEnvelope(response) {
  if (response?.data?.code !== undefined) return response.data;
  if (response?.code !== undefined && response?.data !== undefined) return response;
  return { code: 1000, data: response };
}

function getFailurePayload(error) {
  return error?.response?.data || (error?.code != null ? { code: error.code, data: error.data } : null);
}

function retryConflictMessage(info) {
  const code = String(info?.errorCode || '');
  if (code === 'UNME_RETRY_POLICY_CHANGED') return '재시도 정책이 변경되어 다시 시도할 수 없습니다. 기존 결제내역을 확인해 주세요.';
  if (['UNME_RETRY_CONFIGURATION_CHANGED', 'UNME_RETRY_CONFIGURATION_INVALID'].includes(code)) {
    return '결제 설정이 변경되었거나 확인이 필요합니다. 결제내역을 확인하고 관리자에게 문의해 주세요.';
  }
  if (['UNME_RETRY_PAYMENT_MISMATCH', 'UNME_RETRY_ACTOR_MISMATCH'].includes(code)) {
    return '결제 정보 또는 로그인 상태를 확인할 수 없습니다. 결제내역을 확인하고 관리자에게 문의해 주세요.';
  }
  if (['UNME_RETRY_CONFIRMATION_INVALID_OR_EXPIRED', 'UNME_RETRY_ALREADY_USED_OR_EXPIRED'].includes(code)) {
    return '재시도 확인 정보가 만료되었거나 이미 사용되었습니다. 결제내역을 확인해 주세요.';
  }
  return '결제 결과를 확인 중입니다. 중복 결제하지 말고 결제내역을 확인해 주세요.';
}

// Axios transport codes (e.g. ERR_NETWORK / ECONNABORTED) are not API result codes.
// An absent response, an invalid envelope, or a server error cannot establish whether
// the PG processed the payment, so the user must check the existing transaction.
function isUncertainFailure(error, failure) {
  const httpStatus = Number(error?.response?.status);
  const apiCode = Number(failure?.code);
  return !failure || failure.code == null || !Number.isFinite(apiCode)
    || (Number.isFinite(httpStatus) && httpStatus >= 500)
    || (apiCode >= 5000 && apiCode < 9000) || apiCode === 4090 || String(failure?.data?.status || '').toUpperCase() === 'UNKNOWN'
    || failure?.data?.adminReviewRequired === true;
}

function sessionMatches(domain, scope) {
  try { return Boolean(scope) && getUnmePaymentLoginScope(domain) === scope; } catch (_) { return false; }
}

export default function useUnmeRetryPayment(domain) {
  const enabled = UNME_FALLBACK_ENABLED;
  const [dialog, setDialog] = useState(null);
  const [resolution, setResolution] = useState(null);
  const [notice, setNotice] = useState('');
  const [sending, setSending] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [ready, setReady] = useState(!enabled);
  const [recovery, setRecovery] = useState(null);
  const [checking, setChecking] = useState(false);
  const [awaitingSuccessConfirmation, setAwaitingSuccessConfirmation] = useState(false);
  const scopeRef = useRef(null);
  const orderRef = useRef(null);
  const checkingRef = useRef(false);
  const successfulOrderRef = useRef(false);
  const pending = useRef(null);
  const processingRef = useRef(null);
  const dialogRef = useRef(null);
  const active = useRef(false);
  const mounted = useRef(true);
  const blockedRef = useRef(enabled);

  useEffect(() => {
    mounted.current = true;
    const abandon = () => {
      if (pending.current) {
        const resolve = pending.current;
        pending.current = null;
        resolve('abandon');
      }
    };
    window.addEventListener('pagehide', abandon);
    return () => {
      mounted.current = false;
      window.removeEventListener('pagehide', abandon);
      abandon();
    };
  }, []);

  const checkOrder = useCallback(async () => {
    const order = orderRef.current;
    const scope = scopeRef.current;
    if (!enabled || !order || !scope || checkingRef.current) return;
    checkingRef.current = true;
    if (mounted.current) setChecking(true);
    try {
      if (getUnmePaymentLoginScope(domain) !== scope) throw new Error('Login changed');
      const response = await axios.get(`${process.env.REACT_APP_API || ''}/api/${domain}/payment/manual/order-status`, {
        params: { mchtTrdNo: order }, withCredentials: true, timeout: 15000,
      });
      if (getUnmePaymentLoginScope(domain) !== scope) throw new Error('Login changed');
      const info = response?.data?.data;
      const status = String(info?.status || '').toUpperCase();
      if (response?.data?.code !== 1000 || info?.mchtTrdNo !== order || !['PROCESSING', 'UNKNOWN', 'COMPLETED', 'CANCELED', 'FAILED', 'RETRY_CONFIRMATION_REQUIRED'].includes(status)) throw new Error('Invalid order status');
      const messages = {
        PROCESSING: '결제 요청을 처리 중입니다. 잠시 후 결과를 다시 확인해 주세요.',
        UNKNOWN: '결제 결과를 확인할 수 없습니다. 재결제하지 말고 관리자에게 문의해 주세요.',
        COMPLETED: '기존 결제가 완료되었습니다. 결제내역에서 확인해 주세요.',
        CANCELED: '기존 결제가 취소되었습니다. 결제내역에서 확인해 주세요.',
        FAILED: '기존 결제가 완료되지 않았습니다. 결제내역에서 확인해 주세요.',
        RETRY_CONFIRMATION_REQUIRED: '이전 결제의 재시도 확인이 대기 중입니다. 입력 정보를 복구할 수 없으므로 결제내역과 관리자에게 확인해 주세요.',
      };
      if (mounted.current) setRecovery({ status, message: messages[status] });
    } catch (error) {
      if (mounted.current) setRecovery({ status: 'ERROR', message: error?.message === 'Login changed'
        ? '로그인 정보가 변경되었습니다. 이전 결제의 요청 계정으로 확인해 주세요.'
        : '이전 결제 요청 상태를 확인하지 못했습니다. 재결제하지 말고 결제내역과 관리자에게 확인해 주세요.' });
    } finally {
      checkingRef.current = false;
      if (mounted.current) setChecking(false);
    }
  }, [domain, enabled]);

  useEffect(() => {
    if (!enabled) return;
    try {
      const scope = getUnmePaymentLoginScope(domain);
      const order = readPendingUnmeOrder(domain, scope);
      scopeRef.current = scope;
      orderRef.current = order;
      blockedRef.current = Boolean(order);
      setReady(true);
      if (order) {
        setRecovery({ status: 'PROCESSING', message: '이전 결제 요청을 확인하고 있습니다.' });
        checkOrder();
      }
    } catch (error) {
      blockedRef.current = true;
      setReady(true);
      const status = error?.message === 'Login required' ? 'LOGIN_REQUIRED' : error?.message === 'Login changed' ? 'LOGIN_CHANGED' : 'ERROR';
      setRecovery({ status, message: status === 'LOGIN_REQUIRED'
        ? '결제를 안전하게 이용하려면 다시 로그인해 주세요.'
        : status === 'LOGIN_CHANGED'
          ? '다른 계정의 확인되지 않은 결제가 남아 있습니다. 해당 계정으로 로그인해 확인하거나 관리자에게 문의해 주세요.'
          : '이전 결제 또는 로그인 상태를 확인할 수 없습니다. 재결제하지 말고 관리자에게 문의해 주세요.' });
    }
  }, [domain, enabled, checkOrder]);

  useEffect(() => {
    if (!recovery || recovery.status !== 'PROCESSING') return undefined;
    const timer = window.setTimeout(() => { if (document.visibilityState !== 'hidden') checkOrder(); }, 10000);
    const onVisibility = () => { if (document.visibilityState !== 'hidden') checkOrder(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { window.clearTimeout(timer); document.removeEventListener('visibilitychange', onVisibility); };
  }, [recovery, checkOrder]);

  // Only an explicit action on the displayed success screen can acknowledge a
  // completed order. If the page reloads before that action, retain the order
  // so that the server's result can be recovered without a second payment.
  const acknowledgeSuccessfulPayment = useCallback(() => {
    if (!enabled) return true;
    if (!successfulOrderRef.current || !orderRef.current) return false;
    try {
      clearPendingUnmeOrder(domain, scopeRef.current, orderRef.current);
      orderRef.current = null;
      successfulOrderRef.current = false;
      blockedRef.current = false;
      if (mounted.current) setAwaitingSuccessConfirmation(false);
      return true;
    } catch (_) {
      blockedRef.current = true;
      if (mounted.current) setResolution('결제는 완료되었지만 확인 상태를 저장하지 못했습니다. 결제내역을 확인하고 관리자에게 문의해 주세요.');
      return false;
    }
  }, [domain, enabled]);

  const choose = useCallback((choice) => {
    if (!pending.current) return;
    const resolve = pending.current;
    pending.current = null;
    setDialog(null);
    resolve(choice);
  }, []);

  useLayoutEffect(() => {
    if (!dialog) return undefined;
    dialogRef.current?.focus();
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        choose('cancel');
      }
      if (event.key === 'Tab' && dialogRef.current) {
        const controls = Array.from(dialogRef.current.querySelectorAll('button:not([disabled])'));
        if (!controls.length) { event.preventDefault(); return; }
        const index = controls.indexOf(document.activeElement);
        if (!dialogRef.current.contains(document.activeElement) || (event.shiftKey && index <= 0)) {
          event.preventDefault();
          controls[controls.length - 1].focus();
        } else if (!event.shiftKey && (index === -1 || index === controls.length - 1)) {
          event.preventDefault();
          controls[0].focus();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { window.clearInterval(interval); window.removeEventListener('keydown', onKeyDown); };
  }, [dialog, choose]);

  useEffect(() => {
    if (dialog && now >= dialog.expiresAt) choose('expired');
  }, [dialog, now, choose]);

  useEffect(() => {
    if (sending) processingRef.current?.focus();
  }, [sending]);

  const requireResolution = useCallback((message) => {
    blockedRef.current = true;
    if (mounted.current) setResolution(message || '결과를 확인 중입니다. 중복 결제를 하지 말고 결제내역을 확인해 주세요.');
    throw handled(true);
  }, []);

  const runPayment = useCallback(async (payload, send, context = {}) => {
    if (!enabled) return send(payload);
    if (active.current || blockedRef.current) throw handled(blockedRef.current);
    active.current = true;
    if (mounted.current) setNotice('');
    let original = payload;
    let order = null;
    const clearOrder = () => {
      clearPendingUnmeOrder(domain, scopeRef.current, order);
      orderRef.current = null;
    };
    const sendLocked = async (body) => {
      if (mounted.current) setSending(true);
      try {
        return await send(body);
      } finally {
        if (mounted.current) setSending(false);
      }
    };
    try {
      try {
        if (!scopeRef.current || getUnmePaymentLoginScope(domain) !== scopeRef.current) throw new Error('Login changed');
        order = original?.mchtTrdNo;
        savePendingUnmeOrder(domain, scopeRef.current, order);
        orderRef.current = order;
      } catch (_) {
        return requireResolution('결제 요청을 안전하게 기록하지 못했습니다. 결제내역을 확인하고 관리자에게 문의해 주세요.');
      }
      let response;
      try {
        response = await sendLocked(original);
      } catch (error) {
        const failure = getFailurePayload(error);
        if (isUncertainFailure(error, failure)) {
          return requireResolution(retryConflictMessage(failure?.data));
        }
        try { clearOrder(); } catch (_) { return requireResolution(); }
        throw error;
      }
      if (!mounted.current) throw handled(true);
      if (!sessionMatches(domain, scopeRef.current)) return requireResolution('로그인 정보가 변경되었습니다. 이전 결제의 요청 계정으로 확인해 주세요.');
      let envelope = responseEnvelope(response);
      let info = envelope.data || {};
      let status = String(info.status || '').toUpperCase();
      if ((Number(envelope.code) >= 5000 && Number(envelope.code) < 9000) || Number(envelope.code) === 4090 || status === 'UNKNOWN' || info.adminReviewRequired === true) {
        return requireResolution(retryConflictMessage(info));
      }
      if (Number(envelope.code) !== 1000) return requireResolution('결제 요청 결과를 확인하지 못했습니다. 중복 결제하지 말고 결제내역을 확인해 주세요.');
      if (status === 'RETRY_CONFIRMATION_REQUIRED') {
        if (Number(envelope.code) !== 1000) return requireResolution();
        const token = info.retryConfirmation?.retryToken;
        const expiresAt = Date.parse(info.retryConfirmation?.retryExpiresAt);
        if (typeof token !== 'string' || !token.trim() || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
          return requireResolution('재시도 확인 시간이 만료되었거나 확인 정보를 사용할 수 없습니다. 결제내역을 확인해 주세요.');
        }
        const summary = {
          customer: String(context.customer || original.mchtCustNm || original.pharmacyName || ''),
          amount: Number(original.trdAmt || 0),
          cardLast4: String(original.cardNo || '').slice(-4),
          installment: String(original.instmtMon || '00'),
        };
        setNow(Date.now());
        const selection = new Promise((resolve) => { pending.current = resolve; });
        setDialog({ expiresAt, summary });
        const choice = await selection;
        if (choice === 'abandon') return requireResolution();
        if (choice === 'expired') return requireResolution('재시도 확인 시간이 만료되었습니다. 결제내역을 확인해 주세요.');
        if (choice === 'cancel') {
          if (mounted.current) setSending('cancel');
          try {
            if (getUnmePaymentLoginScope(domain) !== scopeRef.current) throw new Error('Login changed');
            const baseUrl = process.env.REACT_APP_API || '';
            const result = await axios.post(baseUrl + `/api/${domain}/payment/manual/retry-cancel`,
              { unmeRetryToken: token }, { withCredentials: true, timeout: 15000 });
            if (result.data?.code !== 1000 || result.data?.data?.cancelled !== true) throw new Error('cancel not confirmed');
          } catch (_) {
            return requireResolution('재시도 대기 취소를 확인하지 못했습니다. 결제내역을 확인해 주세요.');
          } finally {
            if (mounted.current) setSending(false);
          }
          if (mounted.current) setNotice('재시도를 취소했습니다.');
          try { clearOrder(); } catch (_) { return requireResolution(); }
          throw handled(false);
        }
        if (Date.now() >= expiresAt) return requireResolution('재시도 확인 시간이 만료되었습니다. 결제내역을 확인해 주세요.');
        try {
          if (getUnmePaymentLoginScope(domain) !== scopeRef.current) return requireResolution('로그인 정보가 변경되었습니다. 이전 결제의 요청 계정으로 확인해 주세요.');
          response = await sendLocked({ ...original, unmeRetryToken: token, unmeRetryConfirmed: true });
        } catch (error) {
          const failure = getFailurePayload(error);
          const code = Number(failure?.code);
          if (isUncertainFailure(error, failure)) {
            return requireResolution(retryConflictMessage(failure?.data));
          }
          if (code === 9999) throw error;
          // A submitted confirmation has consumed its token; unresolved errors must not create a new order.
          return requireResolution('재시도 결과를 확인하지 못했습니다. 결제내역을 확인해 주세요.');
        }
        if (!mounted.current) throw handled(true);
        if (!sessionMatches(domain, scopeRef.current)) return requireResolution('로그인 정보가 변경되었습니다. 이전 결제의 요청 계정으로 확인해 주세요.');
        envelope = responseEnvelope(response);
        info = envelope.data || {};
        status = String(info.status || '').toUpperCase();
        if (Number(envelope.code) !== 1000 || status === 'UNKNOWN' || info.adminReviewRequired === true ||
          status === 'RETRY_CONFIRMATION_REQUIRED' || !['COMPLETED', 'FAILED'].includes(status)) {
          return requireResolution(retryConflictMessage(info));
        }
      } else if (Number(envelope.code) === 1000 && !['COMPLETED', 'FAILED'].includes(status)) {
        return requireResolution();
      }
      if (status === 'COMPLETED') {
        successfulOrderRef.current = true;
        blockedRef.current = true;
        if (mounted.current) setAwaitingSuccessConfirmation(true);
      } else {
        try { clearOrder(); } catch (_) { return requireResolution(); }
      }
      return response;
    } finally {
      original = null;
      active.current = false;
    }
  }, [domain, enabled, requireResolution]);

  const postPayment = useCallback((url, payload, options) =>
    runPayment(payload, (body) => axios.post(url, body, enabled ? { ...options, timeout: 90000 } : options)),
  [enabled, runPayment]);

  const remainingSeconds = dialog ? Math.max(0, Math.ceil((dialog.expiresAt - now) / 1000)) : 0;
  const remaining = `${String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}:${String(remainingSeconds % 60).padStart(2, '0')}`;
  const ui = enabled && typeof document !== 'undefined' ? createPortal(
    <>
      {notice && <div role="status" style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 100010, padding: '12px 18px', background: '#263447', color: '#fff', borderRadius: 8 }} onClick={() => setNotice('')}>{notice}</div>}
      {sending && <div ref={processingRef} tabIndex={-1} style={{ ...styles.backdrop, alignItems: 'center' }} role="status" aria-live="polite" onKeyDown={(event) => { if (event.key === 'Tab') event.preventDefault(); }}>
        <div style={styles.processing}>{sending === 'cancel' ? '재시도 대기를 취소하고 있습니다.' : '결제 처리 중입니다. 화면을 닫지 마세요.'}</div>
      </div>}
      {dialog && <div style={styles.backdrop} onClick={() => choose('cancel')}>
        <section ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="unme-retry-title" style={styles.sheet} onClick={(event) => event.stopPropagation()}>
          <button type="button" aria-label="닫기" style={styles.close} onClick={() => choose('cancel')}>×</button>
          <h2 id="unme-retry-title" style={styles.title}>결제를 1회 재시도하시겠습니까?</h2>
          <p style={styles.text}>결제가 거절되었습니다.</p>
          <div style={styles.summary}>
            <span>{dialog.summary.customer || '결제 거래처'}</span>
            <strong style={styles.amount}>{dialog.summary.amount.toLocaleString('ko-KR')}원</strong>
            <span>{dialog.summary.installment === '00' ? '일시불' : `${dialog.summary.installment}개월`} · 카드 끝 {dialog.summary.cardLast4}</span>
          </div>
          <p style={styles.text}>입력한 결제정보로 한 번 더 시도합니다.</p>
          <p style={styles.timer}>선택 가능 시간 {remaining}</p>
          <div style={styles.actions}>
            <button type="button" style={styles.secondary} onClick={() => choose('cancel')}>재시도 안 함</button>
            <button type="button" style={styles.primary} disabled={remainingSeconds <= 0} onClick={() => choose('confirm')}>재시도</button>
          </div>
        </section>
      </div>}
      {resolution && <div style={styles.backdrop}>
        <section role="alertdialog" aria-modal="true" aria-labelledby="unme-resolution-title" style={styles.sheet}>
          <h2 id="unme-resolution-title" style={styles.title}>결과 확인이 필요합니다</h2>
          <p style={styles.text}>{resolution}</p>
          <a style={{ ...styles.primary, display: 'block', textAlign: 'center', textDecoration: 'none' }} href={getUnmePaymentRecoveryPath(domain)}>결제내역 확인</a>
        </section>
      </div>}
      {recovery && <div style={styles.backdrop}>
        <section role="alertdialog" aria-modal="true" aria-labelledby="unme-recovery-title" style={styles.sheet}>
          <h2 id="unme-recovery-title" style={styles.title}>이전 결제 확인</h2>
          <p style={styles.text}>{recovery.message}</p>
          <div style={styles.actions}>
            {orderRef.current && <button type="button" style={styles.secondary} disabled={checking} onClick={checkOrder}>{checking ? '확인 중' : '결과 다시 확인'}</button>}
            {['LOGIN_REQUIRED', 'LOGIN_CHANGED'].includes(recovery.status) && <a style={{ ...styles.secondary, display: 'block', textAlign: 'center', textDecoration: 'none' }} href={domain === 'cp' ? '/cp/login' : '/app/login'}>다시 로그인</a>}
            <a style={{ ...styles.primary, display: 'block', textAlign: 'center', textDecoration: 'none' }} href={getUnmePaymentRecoveryPath(domain)} onClick={(event) => {
              if (!['COMPLETED', 'CANCELED', 'FAILED'].includes(recovery.status)) return;
              try { clearPendingUnmeOrder(domain, scopeRef.current, orderRef.current); orderRef.current = null; }
              catch (_) { event.preventDefault(); setRecovery({ status: 'ERROR', message: '이전 결제를 안전하게 확인하지 못했습니다. 관리자에게 문의해 주세요.' }); }
            }}>결제내역 확인</a>
          </div>
        </section>
      </div>}
    </>, document.body,
  ) : null;
  return { runPayment, postPayment, acknowledgeSuccessfulPayment, retryUi: ui, retryBlocked: !ready || Boolean(resolution) || Boolean(recovery) || awaitingSuccessConfirmation };
}

const styles = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(15,23,42,.55)', display: 'flex', justifyContent: 'center', alignItems: 'flex-end' },
  sheet: { position: 'relative', width: '100%', maxWidth: 520, background: '#fff', borderRadius: '20px 20px 0 0', boxSizing: 'border-box', padding: '30px 24px max(24px, env(safe-area-inset-bottom))', color: '#191f28' },
  close: { position: 'absolute', right: 18, top: 12, background: 'none', border: 0, fontSize: 28, color: '#6b7684', cursor: 'pointer' },
  title: { margin: '0 24px 12px 0', fontSize: 21, fontWeight: 800 },
  text: { margin: '10px 0', color: '#4e5968', fontSize: 14, lineHeight: 1.6 },
  summary: { margin: '18px 0', padding: 16, background: '#f2f4f6', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#4e5968' },
  processing: { background: '#fff', color: '#191f28', borderRadius: 12, padding: '22px 24px', fontWeight: 700, textAlign: 'center' },
  amount: { color: '#191f28', fontSize: 24 },
  timer: { margin: '16px 0', color: '#0064ff', fontWeight: 700, fontSize: 14 },
  actions: { display: 'flex', gap: 10 },
  primary: { flex: 1, border: 0, borderRadius: 9, background: '#0064ff', color: '#fff', fontWeight: 700, padding: '14px 12px', cursor: 'pointer', fontSize: 15 },
  secondary: { flex: 1, border: '1px solid #cbd5e1', borderRadius: 9, background: '#fff', color: '#333d4b', fontWeight: 700, padding: '14px 12px', cursor: 'pointer', fontSize: 15 },
};
